// =============================================================================
// ACT SCIENCE TEST BANK - Form 25MC1
// Source: Official ACT Prep Guide 2025-2026
// Total Questions: 40 (34 scored, 6 field test)
// =============================================================================

export const formId = '25MC1';
export const subject = 'science';
export const totalQuestions = 40;
export const scoredQuestions = 34;
export const timeLimit = 40; // minutes

// Field test questions (not scored) - Questions 29-34
export const fieldTestPositions = [29, 30, 31, 32, 33, 34];

// -----------------------------------------------------------------------------
// SCORING TABLE (34 scored questions)
// -----------------------------------------------------------------------------
export const scoringTable = {
  34: 36, 33: 35, 32: 34, 31: 33, 30: 32, 29: 31, 28: 30, 27: 29, 26: 28, 25: 27,
  24: 26, 23: 25, 22: 25, 21: 24, 20: 23, 19: 23, 18: 22, 17: 21, 16: 20, 15: 19,
  14: 18, 13: 18, 12: 17, 11: 16, 10: 15, 9: 14, 8: 12, 7: 12, 6: 11, 5: 10,
  4: 9, 3: 7, 2: 6, 1: 3, 0: 1
};

// -----------------------------------------------------------------------------
// CATEGORY DEFINITIONS
// -----------------------------------------------------------------------------
export const categories = {
  IOD: { name: 'Interpretation of Data', description: 'Manipulate and analyze data in tables, graphs, and diagrams', count: 16 },
  SIN: { name: 'Scientific Investigation', description: 'Understand experimental tools, procedures, and design', count: 9 },
  EMI: { name: 'Evaluation of Models, Inferences & Experimental Results', description: 'Judge validity of information and formulate conclusions', count: 9 }
};

// -----------------------------------------------------------------------------
// PASSAGE FORMATS
// -----------------------------------------------------------------------------
export const passageFormats = {
  'Data Representation': '26-32% of test',
  'Research Summaries': '50-56% of test',
  'Conflicting Viewpoints': '18-21% of test'
};

// -----------------------------------------------------------------------------
// PASSAGES
// -----------------------------------------------------------------------------
export const passages = [
  {
    id: 'S1',
    title: 'Anole Behavior Study',
    format: 'Data Representation',
    topic: 'Biology - Animal Behavior',
    questionRange: [1, 5],
    description: 'Study comparing green anole and brown anole behavior in different habitat conditions.',
    dataElements: ['Table 1: Habitats', 'Figure 1: Perching height', 'Table 2: Behaviors', 'Table 3: Display times']
  },
  {
    id: 'S2',
    title: 'Antarctic Ice Shelf Calving',
    format: 'Conflicting Viewpoints',
    topic: 'Earth Science - Glaciology',
    questionRange: [6, 11],
    description: 'Four students explain different theories about how icebergs calve from Antarctic ice shelves.'
  },
  {
    id: 'S3',
    title: 'Clownfish Growth Rate',
    format: 'Research Summaries',
    topic: 'Biology - Marine Science',
    questionRange: [12, 17],
    description: 'Two experiments examining how diet and stocking density affect clownfish growth rates.',
    dataElements: ['Table 1: Diet effects on SGR', 'Table 2: Stocking density effects on SGR']
  },
  {
    id: 'S4',
    title: 'Tomato Nutrient Concentration',
    format: 'Research Summaries',
    topic: 'Chemistry - Food Science',
    questionRange: [18, 23],
    description: 'Experiments testing how heating tomatoes affects vitamin C and lycopene concentrations.',
    dataElements: ['Figure 1: Vitamin C concentration', 'Figure 2: Lycopene concentration']
  },
  {
    id: 'S5',
    title: 'Alloy Crystal Formation',
    format: 'Data Representation',
    topic: 'Physics/Chemistry - Materials Science',
    questionRange: [24, 28],
    description: 'Study of how magnetic stirring force affects crystal length in aluminum alloy ingots.',
    dataElements: ['Table 1: Alloy Q composition', 'Figure 1: Crystal length vs magnetic force']
  },
  {
    id: 'S6',
    title: 'DOC Adsorption Study',
    format: 'Research Summaries',
    topic: 'Chemistry - Environmental Science',
    questionRange: [29, 34],
    description: 'Studies examining how clay minerals adsorb dissolved organic carbon from lake water.',
    dataElements: ['Figure 1: CM1 adsorption', 'Figure 2: CM2 adsorption', 'Figure 3: CM3 adsorption']
  },
  {
    id: 'S7',
    title: 'Photoelectric Effect',
    format: 'Research Summaries',
    topic: 'Physics - Quantum Mechanics',
    questionRange: [35, 40],
    description: 'Experiments examining how light frequency and intensity affect electron ejection from metal.',
    dataElements: ['Figure 1: Experimental setup', 'Table 1: Color/frequency/energy/K', 'Table 2: Intensity effects']
  }
];

// -----------------------------------------------------------------------------
// QUESTIONS
// -----------------------------------------------------------------------------
export const questions = [
  // PASSAGE I: Anole Behavior Study (Q1-5)
  {
    id: '25MC1-S01',
    formId: '25MC1',
    questionNumber: 1,
    passageId: 'S1',
    category: 'IOD',
    subcategory: 'Data Analysis',
    difficulty: 2,
    isScored: true,
    question: 'Based on Table 2, which of the following ratios best represents the number of times Behavior 2 was displayed in Habitat X compared to the number of times Behavior 2 was displayed in Habitat Y?',
    choices: [
      { letter: 'A', text: '1:2' },
      { letter: 'B', text: '1:8' },
      { letter: 'C', text: '4:5' },
      { letter: 'D', text: '5:6' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'From Table 2: Behavior 2 in Habitat X = 3, in Habitat Y = 6. The ratio 3:6 simplifies to 1:2.'
  },
  {
    id: '25MC1-S02',
    formId: '25MC1',
    questionNumber: 2,
    passageId: 'S1',
    category: 'IOD',
    subcategory: 'Comparing Data',
    difficulty: 2,
    isScored: true,
    question: 'Which of the following observations for brown anoles was(were) the same in both Habitats Y and Z?\n1. Average perching height\n2. The number of times Behavior 4 was displayed\n3. Average display time for Behavior 5',
    choices: [
      { letter: 'F', text: '1 only' },
      { letter: 'G', text: '3 only' },
      { letter: 'H', text: '1 and 2 only' },
      { letter: 'J', text: '2 and 3 only' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'From Figure 1, brown anoles have the same average perching height (~0.4m) in both Y and Z. Behavior 4 counts differ (5 vs 17), and display times differ (49.6s vs 33.1s).'
  },
  {
    id: '25MC1-S03',
    formId: '25MC1',
    questionNumber: 3,
    passageId: 'S1',
    category: 'IOD',
    subcategory: 'Reading Data',
    difficulty: 2,
    isScored: true,
    question: 'Based on Table 3, how many display times were measured for Behavior 5 in Habitat Z?',
    choices: [
      { letter: 'A', text: '2' },
      { letter: 'B', text: '4' },
      { letter: 'C', text: '12' },
      { letter: 'D', text: 'Cannot be determined from the given information' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Table 3 shows average display times but does not indicate how many measurements were taken.'
  },
  {
    id: '25MC1-S04',
    formId: '25MC1',
    questionNumber: 4,
    passageId: 'S1',
    category: 'IOD',
    subcategory: 'Calculations',
    difficulty: 2,
    isScored: true,
    question: 'Based on Figure 1, for green anoles, the difference in average perching height between Habitat X and Habitat Y was closest to which of the following?',
    choices: [
      { letter: 'F', text: '0.0 m' },
      { letter: 'G', text: '0.3 m' },
      { letter: 'H', text: '0.7 m' },
      { letter: 'J', text: '1.0 m' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'From Figure 1: Green anoles in X ≈ 1.0m, in Y ≈ 1.7m. Difference = 1.7 - 1.0 = 0.7m.'
  },
  {
    id: '25MC1-S05',
    formId: '25MC1',
    questionNumber: 5,
    passageId: 'S1',
    category: 'EMI',
    subcategory: 'Scientific Knowledge',
    difficulty: 2,
    isScored: true,
    question: 'A student claimed that anoles are endotherms. Which of the following explains why this claim is incorrect? Anoles are:',
    choices: [
      { letter: 'A', text: 'amphibians and primarily generate heat from internal metabolic processes to maintain body temperature.' },
      { letter: 'B', text: 'amphibians and primarily absorb heat from the surrounding environment to maintain body temperature.' },
      { letter: 'C', text: 'reptiles and primarily generate heat from internal metabolic processes to maintain body temperature.' },
      { letter: 'D', text: 'reptiles and primarily absorb heat from the surrounding environment to maintain body temperature.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Anoles are reptiles (ectotherms), meaning they absorb heat from their environment rather than generating it internally.'
  },

  // PASSAGE II: Antarctic Ice Shelf Calving - Conflicting Viewpoints (Q6-11)
  {
    id: '25MC1-S06',
    formId: '25MC1',
    questionNumber: 6,
    passageId: 'S2',
    category: 'EMI',
    subcategory: 'Understanding Models',
    difficulty: 2,
    isScored: true,
    question: 'Which of the following diagrams best shows the location of the basal cavity as described by Student 3?',
    choices: [
      { letter: 'F', text: 'Basal cavity is between land and ice shelf, above ocean water' },
      { letter: 'G', text: 'Basal cavity is between land and iceberg, above ocean water' },
      { letter: 'H', text: 'Basal cavity is underneath the ice shelf, at ocean water level' },
      { letter: 'J', text: 'Basal cavity is above the ice shelf, near the iceberg' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Student 3 describes the basal cavity as "the area underneath an ice shelf" where ocean water circulates.'
  },
  {
    id: '25MC1-S07',
    formId: '25MC1',
    questionNumber: 7,
    passageId: 'S2',
    category: 'EMI',
    subcategory: 'Evaluating Claims',
    difficulty: 2,
    isScored: true,
    question: 'Suppose that the air temperature along the Antarctic coastline is never warmer than −10°C and that the atmospheric pressure is always 1.0 atmosphere. Does this information support the description given by Student 1?',
    choices: [
      { letter: 'A', text: 'Yes, because ice cannot melt at those temperatures.' },
      { letter: 'B', text: 'Yes, because ice can melt at those temperatures.' },
      { letter: 'C', text: 'No, because ice cannot melt at those temperatures.' },
      { letter: 'D', text: 'No, because ice can melt at those temperatures.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Student 1 claims surface ice melts due to warming air. At -10°C, ice cannot melt at standard pressure, contradicting Student 1.'
  },
  {
    id: '25MC1-S08',
    formId: '25MC1',
    questionNumber: 8,
    passageId: 'S2',
    category: 'IOD',
    subcategory: 'Scientific Knowledge',
    difficulty: 2,
    isScored: true,
    question: 'Based on the description of the icebergs that are calved along the coastline of Antarctica, do the icebergs sink or float?',
    choices: [
      { letter: 'F', text: 'Sink; they are more dense than ocean water.' },
      { letter: 'G', text: 'Sink; they are less dense than ocean water.' },
      { letter: 'H', text: 'Float; they are more dense than ocean water.' },
      { letter: 'J', text: 'Float; they are less dense than ocean water.' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Ice floats because it is less dense than water. Icebergs are pieces of ice that break off and float in the ocean.'
  },
  {
    id: '25MC1-S09',
    formId: '25MC1',
    questionNumber: 9,
    passageId: 'S2',
    category: 'EMI',
    subcategory: 'Comparing Viewpoints',
    difficulty: 2,
    isScored: true,
    question: 'Which of Students 1 and 4, if either, implied that the processes involved in iceberg calving will take more than one year to result in the formation of an iceberg?',
    choices: [
      { letter: 'A', text: 'Student 1 only' },
      { letter: 'B', text: 'Student 4 only' },
      { letter: 'C', text: 'Both Student 1 and Student 4' },
      { letter: 'D', text: 'Neither Student 1 nor Student 4' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Student 1 mentions "over many summers" and Student 4 mentions "several winter-summer cycles."'
  },
  {
    id: '25MC1-S10',
    formId: '25MC1',
    questionNumber: 10,
    passageId: 'S2',
    category: 'EMI',
    subcategory: 'Comparing Viewpoints',
    difficulty: 2,
    isScored: true,
    question: 'In regard to the season(s) involved in iceberg calving, how does Student 2\'s description differ from Student 3\'s description?',
    choices: [
      { letter: 'F', text: 'Student 2 indicated that summer and winter are involved in calving, whereas Student 3 indicated that only summer is involved in calving.' },
      { letter: 'G', text: 'Student 2 indicated that summer and winter are involved in calving, whereas Student 3 indicated that only winter is involved in calving.' },
      { letter: 'H', text: 'Student 2 indicated that only summer is involved in calving, whereas Student 3 indicated that summer and winter are involved in calving.' },
      { letter: 'J', text: 'Student 2 indicated that only winter is involved in calving, whereas Student 3 indicated that only summer is involved in calving.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Student 2 mentions both summer melting and winter snowfall. Student 3 only mentions summer ocean circulation.'
  },
  {
    id: '25MC1-S11',
    formId: '25MC1',
    questionNumber: 11,
    passageId: 'S2',
    category: 'EMI',
    subcategory: 'Comparing Viewpoints',
    difficulty: 2,
    isScored: true,
    question: 'Which of Students 2, 3, and 4 agree(s) with Student 1 that some form of melting occurs on the ice shelf surface?',
    choices: [
      { letter: 'A', text: 'Student 2 only' },
      { letter: 'B', text: 'Students 2 and 3 only' },
      { letter: 'C', text: 'Students 2 and 4 only' },
      { letter: 'D', text: 'Students 3 and 4 only' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Student 2 agrees about surface melting. Student 4 also mentions surface melting and compaction. Student 3 says melting occurs only from below.'
  },

  // PASSAGE III: Clownfish Growth Rate (Q12-17)
  {
    id: '25MC1-S12',
    formId: '25MC1',
    questionNumber: 12,
    passageId: 'S3',
    category: 'EMI',
    subcategory: 'Interpreting Trends',
    difficulty: 2,
    isScored: true,
    question: 'Which of the following statements about the relationship between the number of A. percula per tank and the average SGR is consistent with the results of Experiment 2?',
    choices: [
      { letter: 'F', text: 'On average, as the number of A. percula per tank increased, the average SGR increased only.' },
      { letter: 'G', text: 'On average, as the number of A. percula per tank increased, the average SGR decreased only.' },
      { letter: 'H', text: 'On average, as the number of A. percula per tank increased, the average SGR remained the same.' },
      { letter: 'J', text: 'On average, as the number of A. percula per tank increased, the average SGR varied with no general trend.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Table 2 shows SGR decreasing (0.50 → 0.35 → 0.25 → 0.20) as stocking density increases.'
  },
  {
    id: '25MC1-S13',
    formId: '25MC1',
    questionNumber: 13,
    passageId: 'S3',
    category: 'EMI',
    subcategory: 'Analyzing Data',
    difficulty: 2,
    isScored: true,
    question: 'The following table gives the percent protein in each of the 4 diets. Which of the following statements about the percent protein and average SGR is consistent with the data?',
    choices: [
      { letter: 'A', text: 'The diet that resulted in the highest average SGR also had the highest percent protein.' },
      { letter: 'B', text: 'The diet that resulted in the highest average SGR had the lowest percent protein.' },
      { letter: 'C', text: 'The diet that resulted in the lowest average SGR had the highest percent protein.' },
      { letter: 'D', text: 'The diet that resulted in the lowest average SGR also had the lowest percent protein.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Diet S had the highest SGR (0.50) with 41.1% protein. Diet Q had the lowest SGR (0.30) with the highest protein (52.5%).'
  },
  {
    id: '25MC1-S14',
    formId: '25MC1',
    questionNumber: 14,
    passageId: 'S3',
    category: 'SIN',
    subcategory: 'Predicting Results',
    difficulty: 2,
    isScored: true,
    question: 'Based on the results of Experiment 1, if Experiment 2 were repeated except that all the A. percula were fed Diet R, would the average SGRs more likely have been lower or higher for each group?',
    choices: [
      { letter: 'F', text: 'Lower; on average, A. percula fed Diet R had an SGR 0.05 percent/day less than those fed Diet T.' },
      { letter: 'G', text: 'Lower; on average, A. percula fed Diet R had an SGR 0.10 percent/day less than those fed Diet T.' },
      { letter: 'H', text: 'Higher; on average, A. percula fed Diet R had an SGR 0.05 percent/day greater than those fed Diet T.' },
      { letter: 'J', text: 'Higher; on average, A. percula fed Diet R had an SGR 0.10 percent/day greater than those fed Diet T.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Diet R: 0.40, Diet T: 0.35. Difference = 0.40 - 0.35 = 0.05 percent/day higher.'
  },
  {
    id: '25MC1-S15',
    formId: '25MC1',
    questionNumber: 15,
    passageId: 'S3',
    category: 'SIN',
    subcategory: 'Experimental Design',
    difficulty: 2,
    isScored: true,
    question: 'Suppose that, in the experiments, 1 g of food were added to each tank at each feeding. A total of how many grams of food would have been placed into an individual tank each day?',
    choices: [
      { letter: 'A', text: '1 g' },
      { letter: 'B', text: '3 g' },
      { letter: 'C', text: '12 g' },
      { letter: 'D', text: '36 g' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'The passage states fish were fed 3 times daily. 1g × 3 feedings = 3g per day.'
  },
  {
    id: '25MC1-S16',
    formId: '25MC1',
    questionNumber: 16,
    passageId: 'S3',
    category: 'SIN',
    subcategory: 'Experimental Design',
    difficulty: 2,
    isScored: true,
    question: 'How many A. percula were placed in each of the tanks in Experiment 1?',
    choices: [
      { letter: 'F', text: '1' },
      { letter: 'G', text: '4' },
      { letter: 'H', text: '10' },
      { letter: 'J', text: '12' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Stocking density was 1 fish/L in 10L of seawater, so 10 fish per tank.'
  },
  {
    id: '25MC1-S17',
    formId: '25MC1',
    questionNumber: 17,
    passageId: 'S3',
    category: 'SIN',
    subcategory: 'Variables',
    difficulty: 2,
    isScored: true,
    question: 'Which of the following was a dependent variable in Experiment 1?',
    choices: [
      { letter: 'A', text: 'Volume of seawater in each tank' },
      { letter: 'B', text: 'Specific growth rate' },
      { letter: 'C', text: 'Diet fed to the A. percula' },
      { letter: 'D', text: 'Stocking density of the A. percula' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'SGR was measured as the outcome (dependent variable). Diet was the independent variable that was manipulated.'
  },

  // PASSAGE IV: Tomato Nutrient Concentration (Q18-23)
  {
    id: '25MC1-S18',
    formId: '25MC1',
    questionNumber: 18,
    passageId: 'S4',
    category: 'SIN',
    subcategory: 'Controls',
    difficulty: 2,
    isScored: true,
    question: 'Which of the samples in Experiment 1 was most likely intended as a control for the concentration of vitamin C present in the unheated tomatoes?',
    choices: [
      { letter: 'F', text: 'Sample 1' },
      { letter: 'G', text: 'Sample 2' },
      { letter: 'H', text: 'Sample 3' },
      { letter: 'J', text: 'Sample 4' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Sample 1 had 0 minutes incubation time, making it the unheated control.'
  },
  {
    id: '25MC1-S19',
    formId: '25MC1',
    questionNumber: 19,
    passageId: 'S4',
    category: 'SIN',
    subcategory: 'Interpolation',
    difficulty: 2,
    isScored: true,
    question: 'Based on the results of Experiment 2, which of the following incubation times would most likely have produced a tomato mixture with a lycopene concentration between 5 mg/g tomato and 6 mg/g tomato?',
    choices: [
      { letter: 'A', text: '0 min' },
      { letter: 'B', text: '0.2 min' },
      { letter: 'C', text: '2 min' },
      { letter: 'D', text: '20 min' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Figure 2 shows lycopene concentration increases with heating time. Sample 3 (15 min) ≈ 4.5, Sample 4 (30 min) > 5.5. 20 min would fall between.'
  },
  {
    id: '25MC1-S20',
    formId: '25MC1',
    questionNumber: 20,
    passageId: 'S4',
    category: 'EMI',
    subcategory: 'Evaluating Claims',
    difficulty: 2,
    isScored: true,
    question: 'A student claimed that heating tomatoes decreases the concentration of nutrients present. This claim is consistent with the results shown for which of vitamin C and lycopene, if either?',
    choices: [
      { letter: 'F', text: 'Vitamin C only' },
      { letter: 'G', text: 'Lycopene only' },
      { letter: 'H', text: 'Both vitamin C and lycopene' },
      { letter: 'J', text: 'Neither vitamin C nor lycopene' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Figure 1 shows vitamin C decreases with heating. Figure 2 shows lycopene increases with heating.'
  },
  {
    id: '25MC1-S21',
    formId: '25MC1',
    questionNumber: 21,
    passageId: 'S4',
    category: 'SIN',
    subcategory: 'Scientific Knowledge',
    difficulty: 2,
    isScored: true,
    question: 'Assume that the water bath contained pure water at standard atmospheric pressure (1 atm). While the bags were being incubated, was the water most likely boiling?',
    choices: [
      { letter: 'A', text: 'Yes; the incubation temperature was less than the boiling point of water at 1 atm.' },
      { letter: 'B', text: 'Yes; the incubation temperature was greater than the boiling point of water at 1 atm.' },
      { letter: 'C', text: 'No; the incubation temperature was less than the boiling point of water at 1 atm.' },
      { letter: 'D', text: 'No; the incubation temperature was greater than the boiling point of water at 1 atm.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'The incubation temperature was 88°C, which is less than 100°C (boiling point at 1 atm).'
  },
  {
    id: '25MC1-S22',
    formId: '25MC1',
    questionNumber: 22,
    passageId: 'S4',
    category: 'IOD',
    subcategory: 'Reading Data',
    difficulty: 2,
    isScored: true,
    question: 'In Experiment 1, how many of the samples had a vitamin C concentration of less than 1.0 μmol/g tomato?',
    choices: [
      { letter: 'F', text: '0' },
      { letter: 'G', text: '1' },
      { letter: 'H', text: '3' },
      { letter: 'J', text: '4' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Figure 1 shows all 4 samples had vitamin C concentrations below 1.0 μmol/g (ranging from about 0.35 to 0.75).'
  },
  {
    id: '25MC1-S23',
    formId: '25MC1',
    questionNumber: 23,
    passageId: 'S4',
    category: 'SIN',
    subcategory: 'Procedure',
    difficulty: 2,
    isScored: true,
    question: 'Consider the following procedures performed in Experiment 2 for Sample 2:\n1. The sample was frozen.\n2. The sample was incubated in the water bath.\n3. The sample and solvent mixture was filtered.\nThese procedures were performed in what order?',
    choices: [
      { letter: 'A', text: '1, 2, 3' },
      { letter: 'B', text: '1, 3, 2' },
      { letter: 'C', text: '2, 1, 3' },
      { letter: 'D', text: '2, 3, 1' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'The procedure was: incubate → freeze → (later) thaw, add solvent, filter.'
  },

  // PASSAGE V: Alloy Crystal Formation (Q24-28)
  {
    id: '25MC1-S24',
    formId: '25MC1',
    questionNumber: 24,
    passageId: 'S5',
    category: 'IOD',
    subcategory: 'Graph Analysis',
    difficulty: 2,
    isScored: true,
    question: 'Based on Figure 1, for Alloy Q initially at a temperature of 550°C, which of the following ranges of F best represents a linear region?',
    choices: [
      { letter: 'F', text: 'Between 0 and 10 × 10⁷' },
      { letter: 'G', text: 'Between 10 × 10⁷ and 20 × 10⁷' },
      { letter: 'H', text: 'Between 20 × 10⁷ and 30 × 10⁷' },
      { letter: 'J', text: 'Between 30 × 10⁷ and 40 × 10⁷' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'The 550°C curve appears most linear (straight) in the range from 30 to 40 × 10⁷.'
  },
  {
    id: '25MC1-S25',
    formId: '25MC1',
    questionNumber: 25,
    passageId: 'S5',
    category: 'IOD',
    subcategory: 'Extrapolation',
    difficulty: 2,
    isScored: true,
    question: 'Consider the 2 trends shown for Alloy Q initially at 280°C and 550°C, from F = 40 × 10⁷ through F = 48 × 10⁷. If these lines were to continue along the same trend, at which value of F would the average crystal lengths most likely be the same?',
    choices: [
      { letter: 'A', text: 'F = 50 × 10⁷' },
      { letter: 'B', text: 'F = 60 × 10⁷' },
      { letter: 'C', text: 'F = 70 × 10⁷' },
      { letter: 'D', text: 'F = 80 × 10⁷' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'The two curves are converging and would intersect around F = 60 × 10⁷ if extended.'
  },
  {
    id: '25MC1-S26',
    formId: '25MC1',
    questionNumber: 26,
    passageId: 'S5',
    category: 'IOD',
    subcategory: 'Data Interpretation',
    difficulty: 2,
    isScored: true,
    question: 'Based on Figure 1, which combination of values for initial temperature and F would produce the shortest average crystal length in an ingot of Alloy Q?',
    choices: [
      { letter: 'F', text: '280°C and F = 10 × 10⁷' },
      { letter: 'G', text: '280°C and F = 40 × 10⁷' },
      { letter: 'H', text: '550°C and F = 10 × 10⁷' },
      { letter: 'J', text: '550°C and F = 40 × 10⁷' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Figure 1 shows the lowest L (≈88 μm) occurs at 550°C initial temperature with high F values around 40 × 10⁷.'
  },
  {
    id: '25MC1-S27',
    formId: '25MC1',
    questionNumber: 27,
    passageId: 'S5',
    category: 'IOD',
    subcategory: 'Calculations',
    difficulty: 2,
    isScored: true,
    question: 'The following table lists the mass of silicon in 50 g samples of 4 different alloys. Given the composition of Alloy Q, which sample is most likely Alloy Q?',
    choices: [
      { letter: 'A', text: 'Sample W (0.11 g Si)' },
      { letter: 'B', text: 'Sample X (0.14 g Si)' },
      { letter: 'C', text: 'Sample Y (2.7 g Si)' },
      { letter: 'D', text: 'Sample Z (5.4 g Si)' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Alloy Q is 10.8% Si. In 50g: 50 × 0.108 = 5.4g Si. Sample Z matches.'
  },
  {
    id: '25MC1-S28',
    formId: '25MC1',
    questionNumber: 28,
    passageId: 'S5',
    category: 'IOD',
    subcategory: 'Calculations',
    difficulty: 2,
    isScored: true,
    question: 'Based on Table 1, if an ingot of Alloy Q had a mass of 200 g, that ingot would contain what mass of Mg?',
    choices: [
      { letter: 'F', text: '0.22 g' },
      { letter: 'G', text: '0.44 g' },
      { letter: 'H', text: '2.2 g' },
      { letter: 'J', text: '4.4 g' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Alloy Q is 0.22% Mg. In 200g: 200 × 0.0022 = 0.44g Mg.'
  },

  // PASSAGE VI: DOC Adsorption Study (Q29-34) - FIELD TEST
  {
    id: '25MC1-S29',
    formId: '25MC1',
    questionNumber: 29,
    passageId: 'S6',
    category: 'IOD',
    subcategory: 'Comparing Data',
    difficulty: 2,
    isScored: false,
    question: 'Based on the results of the studies, from which of the 5 leachates was the greatest percent of DOC adsorbed by CM1, CM2, and CM3, respectively?',
    choices: [
      { letter: 'A', text: 'CM1: maple; CM2: maple; CM3: rhododendron' },
      { letter: 'B', text: 'CM1: oak; CM2: pine; CM3: magnolia' },
      { letter: 'C', text: 'CM1: pine; CM2: magnolia; CM3: rhododendron' },
      { letter: 'D', text: 'CM1: pine; CM2: magnolia; CM3: magnolia' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'From the figures: CM1 highest for pine (~55%), CM2 highest for magnolia (~50%), CM3 highest for magnolia (~35%).'
  },
  {
    id: '25MC1-S30',
    formId: '25MC1',
    questionNumber: 30,
    passageId: 'S6',
    category: 'IOD',
    subcategory: 'Averages',
    difficulty: 2,
    isScored: false,
    question: 'Based on the results of Study 3, the percent of leachate DOC adsorbed by CM3, averaged across the 5 types of leaves, is closest to which of the following?',
    choices: [
      { letter: 'F', text: '10%' },
      { letter: 'G', text: '20%' },
      { letter: 'H', text: '30%' },
      { letter: 'J', text: '40%' }
    ],
    correctIndex: 1,
    correctLetter: 'J',
    explanation: 'From Figure 3, approximate values: maple ~7%, oak ~17%, pine ~25%, magnolia ~35%, rhododendron ~20%. Average ≈ 20%.'
  },
  {
    id: '25MC1-S31',
    formId: '25MC1',
    questionNumber: 31,
    passageId: 'S6',
    category: 'IOD',
    subcategory: 'Comparing Data',
    difficulty: 2,
    isScored: false,
    question: 'Is the statement "CM2 adsorbed a greater percent of the DOC in the maple leachate than did CM3" supported by the results of Studies 2 and 3?',
    choices: [
      { letter: 'A', text: 'Yes; CM2 adsorbed 35% of the leachate DOC, whereas CM3 adsorbed 7%.' },
      { letter: 'B', text: 'Yes; CM2 adsorbed 55% of the leachate DOC, whereas CM3 adsorbed 17%.' },
      { letter: 'C', text: 'No; CM2 adsorbed 7% of the leachate DOC, whereas CM3 adsorbed 35%.' },
      { letter: 'D', text: 'No; CM2 adsorbed 17% of the leachate DOC, whereas CM3 adsorbed 55%.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'From the figures, CM2 adsorbed less maple DOC (~7%) than CM3 (~35% estimated from other values).'
  },
  {
    id: '25MC1-S32',
    formId: '25MC1',
    questionNumber: 32,
    passageId: 'S6',
    category: 'IOD',
    subcategory: 'Reading Data',
    difficulty: 2,
    isScored: false,
    question: 'Based on the results of the studies, which of the 3 clay minerals, if any, reduced the DOC in the oak leachate by more than 50%?',
    choices: [
      { letter: 'F', text: 'CM1 only' },
      { letter: 'G', text: 'CM2 only' },
      { letter: 'H', text: 'CM1 and CM3 only' },
      { letter: 'J', text: 'None of the 3 clay minerals' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'None of the clay minerals adsorbed more than 50% of the oak leachate DOC based on the figures.'
  },
  {
    id: '25MC1-S33',
    formId: '25MC1',
    questionNumber: 33,
    passageId: 'S6',
    category: 'EMI',
    subcategory: 'Scientific Knowledge',
    difficulty: 2,
    isScored: false,
    question: 'Is a mixture of any one of the leachates and any one of the clay minerals properly considered a solution?',
    choices: [
      { letter: 'A', text: 'Yes, because the clay mineral particles are dissolved in the leachate.' },
      { letter: 'B', text: 'Yes, because the clay mineral particles are not dissolved in the leachate.' },
      { letter: 'C', text: 'No, because the clay mineral particles are dissolved in the leachate.' },
      { letter: 'D', text: 'No, because the clay mineral particles are not dissolved in the leachate.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'A solution requires dissolved particles. Clay mineral particles are suspended, not dissolved, making it a suspension, not a solution.'
  },
  {
    id: '25MC1-S34',
    formId: '25MC1',
    questionNumber: 34,
    passageId: 'S6',
    category: 'SIN',
    subcategory: 'Procedure',
    difficulty: 2,
    isScored: false,
    question: 'In lake water, DOC is broken down into simpler compounds by electromagnetic energy in the visible wavelength range. What action was taken in the studies to prevent this process from occurring?',
    choices: [
      { letter: 'F', text: 'Each mixture of leaves and filtered lake water was placed in the dark.' },
      { letter: 'G', text: 'Each mixture of filtrate and clay mineral was placed in the dark.' },
      { letter: 'H', text: 'Each mixture of leaves and lake water was filtered.' },
      { letter: 'J', text: 'Each mixture of leachate and clay mineral was filtered.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'The passage states the mixture was "placed in the dark" to prevent light (visible electromagnetic energy) from breaking down DOC.'
  },

  // PASSAGE VII: Photoelectric Effect (Q35-40) - SCORED
  {
    id: '25MC1-S35',
    formId: '25MC1',
    questionNumber: 35,
    passageId: 'S7',
    category: 'IOD',
    subcategory: 'Reading Data',
    difficulty: 2,
    isScored: true,
    question: 'Consider the current shown on the ammeter in Figure 1. Based on the results of Experiment 2, when this current was measured, what was the relative intensity of the light?',
    choices: [
      { letter: 'A', text: '100%' },
      { letter: 'B', text: '50%' },
      { letter: 'C', text: '25%' },
      { letter: 'D', text: '12.5%' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Figure 1 shows 19.8 mA. Table 2 shows 50% intensity produces 19.8 mA current.'
  },
  {
    id: '25MC1-S36',
    formId: '25MC1',
    questionNumber: 36,
    passageId: 'S7',
    category: 'SIN',
    subcategory: 'Experimental Design',
    difficulty: 2,
    isScored: true,
    question: 'What aspect of the experimental setup was held constant in Experiment 2 but not in Experiment 1?',
    choices: [
      { letter: 'F', text: 'Color of light' },
      { letter: 'G', text: 'Light source' },
      { letter: 'H', text: 'Type of metal plate' },
      { letter: 'J', text: 'Distance between metal plate and electrode' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Experiment 2 used no filter (constant color/frequency), while Experiment 1 varied colors using different filters.'
  },
  {
    id: '25MC1-S37',
    formId: '25MC1',
    questionNumber: 37,
    passageId: 'S7',
    category: 'IOD',
    subcategory: 'Diagram Interpretation',
    difficulty: 2,
    isScored: true,
    question: 'Based on Figure 1, are the particles ejected from the metal plate moving toward the electrode or away from the electrode, and are those particles positively charged or negatively charged?',
    choices: [
      { letter: 'A', text: 'Toward; positively charged' },
      { letter: 'B', text: 'Toward; negatively charged' },
      { letter: 'C', text: 'Away from; positively charged' },
      { letter: 'D', text: 'Away from; negatively charged' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Figure 1 shows electrons moving toward the electrode. Electrons are negatively charged.'
  },
  {
    id: '25MC1-S38',
    formId: '25MC1',
    questionNumber: 38,
    passageId: 'S7',
    category: 'IOD',
    subcategory: 'Calculations',
    difficulty: 2,
    isScored: true,
    question: 'Based on the equation in the passage (K = E − M) and the results of Experiment 1, what was the value of M for the metal plate used in the setup?',
    choices: [
      { letter: 'F', text: '2.14 eV' },
      { letter: 'G', text: '2.20 eV' },
      { letter: 'H', text: '2.31 eV' },
      { letter: 'J', text: '2.42 eV' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Using K = E − M, rearrange to M = E − K. For green light: M = 2.31 − 0.11 = 2.20 eV.'
  },
  {
    id: '25MC1-S39',
    formId: '25MC1',
    questionNumber: 39,
    passageId: 'S7',
    category: 'IOD',
    subcategory: 'Data Interpretation',
    difficulty: 2,
    isScored: true,
    question: 'The cutoff frequency for a particular metal is the lowest frequency of light at which electrons are ejected from the metal. Based on the results of Experiment 1, the cutoff frequency for the metal plate was:',
    choices: [
      { letter: 'A', text: 'less than 4.4 × 10¹⁴ Hz.' },
      { letter: 'B', text: 'between 4.4 × 10¹⁴ Hz and 5.2 × 10¹⁴ Hz.' },
      { letter: 'C', text: 'between 5.2 × 10¹⁴ Hz and 5.6 × 10¹⁴ Hz.' },
      { letter: 'D', text: 'greater than 5.6 × 10¹⁴ Hz.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Yellow light (5.2 × 10¹⁴ Hz) ejected no electrons (N.A.), but green (5.6 × 10¹⁴ Hz) did. Cutoff is between these.'
  },
  {
    id: '25MC1-S40',
    formId: '25MC1',
    questionNumber: 40,
    passageId: 'S7',
    category: 'IOD',
    subcategory: 'Formulas',
    difficulty: 2,
    isScored: true,
    question: 'The relationship between E and the frequency of light is given by E = hf. Based on the data for green light in Table 1, which expression could be used to determine the value of h?',
    choices: [
      { letter: 'F', text: '(5.6 × 10¹⁴ Hz) / (0.11 eV)' },
      { letter: 'G', text: '(0.11 eV) / (5.6 × 10¹⁴ Hz)' },
      { letter: 'H', text: '(5.6 × 10¹⁴ Hz) / (2.31 eV)' },
      { letter: 'J', text: '(2.31 eV) / (5.6 × 10¹⁴ Hz)' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'E = hf, so h = E/f. For green light: h = 2.31 eV / (5.6 × 10¹⁴ Hz).'
  }
];

// -----------------------------------------------------------------------------
// METADATA
// -----------------------------------------------------------------------------
export const metadata = {
  formId: '25MC1',
  subject: 'science',
  source: 'Official ACT Prep Guide 2025-2026',
  totalQuestions: 40,
  scoredQuestions: 34,
  fieldTestCount: 6,
  timeLimit: 40,
  passageCount: 7,
  categories: ['IOD', 'SIN', 'EMI'],
  categoryBreakdown: {
    IOD: 16,
    SIN: 9,
    EMI: 9
  }
};

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------
export function getQuestionsByPassage(passageId) {
  return questions.filter(q => q.passageId === passageId);
}

export function getScoredQuestions() {
  return questions.filter(q => q.isScored);
}

export function getQuestionsByCategory(category) {
  return questions.filter(q => q.category === category);
}

export function calculateScaledScore(rawScore) {
  return scoringTable[rawScore] || 1;
}

console.log(`Science Test Bank 25MC1 Loaded: ${questions.length} questions (${getScoredQuestions().length} scored)`);
